import { collection, doc, getDoc, getDocs, limit, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { expandPermissionKeys } from './permissionBridge';

/** Converte mapa `{ patients_view: true, ... }` em lista de chaves ativas. */
export function permissionsObjectToKeys(permissions: unknown): string[] {
  if (!permissions || typeof permissions !== 'object') return [];
  return Object.entries(permissions as Record<string, unknown>)
    .filter(([, v]) => v === true)
    .map(([k]) => k);
}

export interface ResolvePermissionsOptions {
  email?: string | null;
  clinicId?: string | null;
}

/** Defaults por role (fallback quando não há clinic_system_users / papel). */
export function getDefaultPermissionsByRole(role: string): string[] {
  const normalized = normalizeRole(role);
  switch (normalized) {
    case 'admin':
      return ['all'];
    case 'professional':
      return [
        'view_patients',
        'patients_edit',
        'patients_delete',
        'manage_appointments',
        'telemedicine',
        'manage_ehr',
        'therapeutic_attendance.view',
        'therapeutic_attendance.create_class',
        'therapeutic_attendance.edit_class',
        'therapeutic_attendance.mark_attendance',
        'therapeutic_attendance.correct_attendance',
        'therapeutic_attendance.close_record',
        'therapeutic_attendance.export_reports',
        'therapeutic_attendance.manage_requests',
        'billing.managePlans',
        'billing.viewSubscriptions',
        'billing.runAutomation',
        'financial.view',
        'financial.edit',
        'ai.copilot.use',
        'ai.audit.view',
        'ai.governance.view',
        'ai.governance.edit',
      ];
    case 'secretary':
    case 'recepcao':
      return [
        'view_patients',
        'patients_edit',
        'manage_appointments',
        'view_ehr',
        'therapeutic_attendance.view',
        'therapeutic_attendance.mark_attendance',
        'therapeutic_attendance.export_reports',
        'billing.viewSubscriptions',
        'financial.view',
      ];
    case 'assistant':
      return [
        'view_patients',
        'patients_edit',
        'view_appointments',
        'view_ehr',
        'therapeutic_attendance.view',
        'therapeutic_attendance.mark_attendance',
      ];
    case 'intern':
      return ['view_patients', 'view_appointments', 'view_ehr', 'therapeutic_attendance.view'];
    case 'financeiro':
      return [
        'view_patients',
        'view_appointments',
        'billing.managePlans',
        'billing.viewSubscriptions',
        'billing.runAutomation',
        'financial.view',
        'financial.edit',
        'ai.copilot.use',
        'ai.audit.view',
      ];
    case 'patient':
      return ['view_own_ehr', 'view_own_appointments'];
    default:
      return [];
  }
}

function normalizeRole(role: string): string {
  const valid = [
    'admin',
    'professional',
    'secretary',
    'assistant',
    'intern',
    'patient',
    'financeiro',
    'recepcao',
  ];
  if (!role || !valid.includes(role)) return 'professional';
  return role;
}

async function fetchSystemUserCatalogKeys(
  uid: string,
  options?: ResolvePermissionsOptions
): Promise<string[]> {
  const clinicId = options?.clinicId?.trim();
  if (!clinicId) return [];

  try {
    let userDoc: { permissionRoleId?: string; uid?: string } | null = null;
    let userDocId: string | null = null;

    const byUid = await getDocs(
      query(
        collection(db, 'clinic_system_users'),
        where('clinicId', '==', clinicId),
        where('uid', '==', uid),
        limit(3)
      )
    );
    if (!byUid.empty) {
      userDocId = byUid.docs[0].id;
      userDoc = byUid.docs[0].data() as any;
    } else if (options?.email?.trim()) {
      const byEmail = await getDocs(
        query(
          collection(db, 'clinic_system_users'),
          where('clinicId', '==', clinicId),
          where('email', '==', options.email.trim().toLowerCase()),
          limit(3)
        )
      );
      if (!byEmail.empty) {
        userDocId = byEmail.docs[0].id;
        userDoc = byEmail.docs[0].data() as any;
        // Link Firebase Auth uid so future lookups by uid work
        if (userDocId && !userDoc?.uid) {
          try {
            await updateDoc(doc(db, 'clinic_system_users', userDocId), { uid });
          } catch {
            /* best-effort */
          }
        }
      }
    }

    const roleId = userDoc?.permissionRoleId?.trim();
    if (!roleId) return [];

    if (roleId === 'master' || roleId === 'admin' || roleId.endsWith('__master')) return ['all'];

    const roleSnap = await getDoc(doc(db, 'clinic_permission_roles', roleId));
    if (!roleSnap.exists()) {
      const roleQuery = await getDocs(
        query(collection(db, 'clinic_permission_roles'), where('clinicId', '==', clinicId), limit(50))
      );
      const match = roleQuery.docs.find((d) => d.id === roleId || d.data()?.id === roleId);
      if (!match) return [];
      const mdata = match.data() as any;
      if (mdata?.isMaster || String(mdata?.title || '').toLowerCase() === '#master') return ['all'];
      const keys = (mdata?.permissions || mdata?.keys || []) as string[];
      return Array.isArray(keys) ? keys : permissionsObjectToKeys(keys);
    }

    const data = roleSnap.data() as any;
    if (data.isMaster || String(data.title || '').toLowerCase() === '#master') return ['all'];
    const keys = (data.permissions || data.keys || []) as string[];
    return Array.isArray(keys) ? keys : permissionsObjectToKeys(keys);
  } catch {
    return [];
  }
}

async function fetchLegacyCustomPermissions(
  uid: string,
  options?: ResolvePermissionsOptions
): Promise<string[]> {
  const merged: string[] = [];

  const [userAuthResult, profByUidResult] = await Promise.allSettled([
    getDoc(doc(db, 'userAuth', uid)),
    getDoc(doc(db, 'professionals', uid)),
  ]);

  if (userAuthResult.status === 'fulfilled' && userAuthResult.value.exists()) {
    merged.push(...permissionsObjectToKeys(userAuthResult.value.data()?.permissions));
  }

  if (profByUidResult.status === 'fulfilled' && profByUidResult.value.exists()) {
    merged.push(...permissionsObjectToKeys(profByUidResult.value.data()?.permissions));
  }

  const email = options?.email?.trim();
  const clinicId = options?.clinicId?.trim();

  if (email && clinicId) {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'professionals'),
          where('clinicId', '==', clinicId),
          where('email', '==', email),
          limit(5)
        )
      );
      for (const d of snap.docs) {
        merged.push(...permissionsObjectToKeys(d.data()?.permissions));
      }
    } catch {
      /* query pode falhar por índice */
    }
  }

  for (const field of ['uid', 'userId'] as const) {
    try {
      const snap = await getDocs(
        query(collection(db, 'professionals'), where(field, '==', uid), limit(5))
      );
      for (const d of snap.docs) {
        merged.push(...permissionsObjectToKeys(d.data()?.permissions));
      }
    } catch {
      /* ignore */
    }
  }

  return expandPermissionKeys([...new Set(merged)]);
}

/**
 * Carrega permissões granulares mesclando fontes legadas + catálogo.
 * Preferir `resolveUserPermissions` (fonte de verdade).
 */
export async function fetchMergedCustomPermissions(
  uid: string,
  options?: ResolvePermissionsOptions
): Promise<string[]> {
  const [legacy, catalogKeys] = await Promise.all([
    fetchLegacyCustomPermissions(uid, options),
    fetchSystemUserCatalogKeys(uid, options),
  ]);
  return expandPermissionKeys([...new Set([...legacy, ...catalogKeys])]);
}

export function mergeRolePermissions(
  defaultPermissions: string[],
  customPermissions: string[],
  options?: { mode?: 'union' | 'replace-if-custom' }
): string[] {
  if (customPermissions.includes('all')) return ['all'];
  const mode = options?.mode || 'union';
  if (mode === 'replace-if-custom' && customPermissions.length > 0) {
    return [...new Set(customPermissions)];
  }
  return [...new Set([...defaultPermissions, ...customPermissions])];
}

/** Titular da clínica: perfil próprio, sem vínculo de subordinado e sem papel de paciente/admin. */
export function isOwnerProfileData(data: any): boolean {
  if (!data || !data.clinicId) return false;
  if (data.isSubordinate || data.adminId) return false;
  const role = String(data.role || '').toLowerCase();
  return role !== 'patient' && role !== 'admin';
}

async function isClinicOwnerAccount(uid: string): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() && isOwnerProfileData(snap.data());
  } catch {
    return false;
  }
}

/**
 * Fonte de verdade das permissões em runtime:
 * 1) clinic_system_users → clinic_permission_roles (substitui defaults)
 * 2) admin ou dono da clínica sem papel de sistema → ['all']
 * 3) defaults do role + overlays legados (userAuth / professionals)
 */
export async function resolveUserPermissions(input: {
  uid: string;
  email?: string | null;
  clinicId?: string | null;
  usersRole?: string | null;
}): Promise<string[]> {
  const { uid, email, clinicId, usersRole } = input;
  const role = normalizeRole(usersRole || '');

  try {
    const catalogKeys = await fetchSystemUserCatalogKeys(uid, { email, clinicId });
    if (catalogKeys.length > 0) {
      const expanded = expandPermissionKeys(catalogKeys);
      if (expanded.includes('all')) return ['all'];
      return [...new Set(expanded)];
    }

    if (role === 'admin') return ['all'];

    // Dono da clínica sem papel de sistema = master. Antes recebia só as permissões padrão de
    // profissional e ficava sem Home Care, entre outros (BUG-123; 12 de 13 donos em produção).
    if (await isClinicOwnerAccount(uid)) return ['all'];

    const defaults = getDefaultPermissionsByRole(role);
    const legacy = await fetchLegacyCustomPermissions(uid, { email, clinicId });
    // Sem papel de sistema: defaults + overlays legados (união)
    return mergeRolePermissions(defaults, legacy, { mode: 'union' });
  } catch (error) {
    console.warn('⚠️ [PERMISSIONS] Falha ao resolver; usando defaults do role:', error);
    return getDefaultPermissionsByRole(role);
  }
}
